/**
 * `<wlsn-align-items-height>` — aligns `.js-align-item` heights inside the host (lifecycle: connect / disconnect).
 *
 * Host attributes (same as former trigger `data-*`):
 * - `data-wlsn-layout`: `grid` (default) or `slider` (no column chunking when column attrs are set; clears inline heights).
 * - `data-wlsn-align-mob` / `tab` / `desk` / `cols` — column counts; empty mob–desk + no `cols` ⇒ global max height for all items.
 *
 * Markup: optional `.js-align-group` columns; `.js-align-item` cells (`data-index` or sibling order for row keys).
 *
 * Cross-component refresh: `document.dispatchEvent(new CustomEvent('wlsn:align-items-height:refresh', { bubbles: true, detail: { root?: HTMLElement } }))` — when `detail.root` is set, only that host runs; when omitted, every instance aligns.
 *
 * After the first successful alignment, ResizeObserver and the document refresh listener are removed so tab-driven
 * layout changes do not re-run `align()`. **Window `resize` stays** so column breakpoints / widths can update heights.
 * Call `element.refresh()` for other manual remeasure needs.
 */

if (!customElements.get('wlsn-align-items-height')) {
  class WLSNAlignItemsHeight extends HTMLElement {
    /**
     * Viewport tiers for `data-wlsn-align-mob` / `tab` / `desk` (px).
     * @type {{ tab: number, desk: number }}
     */
    static BREAKPOINTS = Object.freeze({
      tab: 768,
      desk: 1024,
    });

    constructor() {
      super();

      this.classes = {
        group: 'js-align-group',
        item: 'js-align-item',
      };

      /** @type {ResizeObserver | null} */
      this._resizeObserver = null;

      /** @type {ReturnType<typeof setTimeout> | null} */
      this._resizeTimeout = null;

      /** @type {(() => void) | null} */
      this._debouncedAlign = null;

      /** @type {boolean} */
      this._alignmentFinalized = false;

      this.bind = {
        onWindowResize: this._onWindowResize.bind(this),
        onResizeObserver: this._onResizeObserver.bind(this),
        onDocRefresh: this._onDocRefresh.bind(this),
      };
    }

    /**
     * Scoped group and item nodes under this host.
     * @returns {{ groups: NodeListOf<HTMLElement>, items: NodeListOf<HTMLElement> }}
     */
    get elements() {
      return {
        groups: this.querySelectorAll(`.${this.classes.group}`),
        items: this.querySelectorAll(`.${this.classes.item}`),
      };
    }

    /**
     * Wires ResizeObserver, resize + document refresh listeners, and schedules the first align pass.
     * @returns {void}
     */
    connectedCallback() {
      const debounceMs = 150;
      const helpers = window.WLSNHelpers;
      this._debouncedAlign =
        helpers && typeof helpers.debounce === 'function'
          ? helpers.debounce(() => {
              this._alignImpl(this._alignmentFinalized);
            }, debounceMs)
          : () => {
              if (this._resizeTimeout) {
                clearTimeout(this._resizeTimeout);
              }
              this._resizeTimeout = setTimeout(() => {
                this._alignImpl(this._alignmentFinalized);
                this._resizeTimeout = null;
              }, debounceMs);
            };

      this._resizeObserver = new ResizeObserver(this.bind.onResizeObserver);
      const { groups } = this.elements;
      if (groups.length) {
        groups.forEach((group) => {
          this._resizeObserver.observe(group);
        });
      } else {
        this._resizeObserver.observe(this);
      }

      window.addEventListener('resize', this.bind.onWindowResize);
      document.addEventListener('wlsn:align-items-height:refresh', this.bind.onDocRefresh);

      this._scheduleInitialAlign();
    }

    /**
     * Runs `align` after the next frame(s) so layout is more likely settled.
     * @returns {void}
     */
    _scheduleInitialAlign() {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.align();
        });
      });
    }

    /**
     * Drops observers, listeners, and clears inline heights.
     * @returns {void}
     */
    disconnectedCallback() {
      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
        this._resizeObserver = null;
      }

      if (this._resizeTimeout) {
        clearTimeout(this._resizeTimeout);
        this._resizeTimeout = null;
      }

      window.removeEventListener('resize', this.bind.onWindowResize);
      document.removeEventListener('wlsn:align-items-height:refresh', this.bind.onDocRefresh);

      this._resetHeights();
    }

    /**
     * Re-runs alignment even after internal listeners were torn down (e.g. new cards via AJAX).
     * @returns {void}
     */
    refresh() {
      this._alignImpl(true);
    }

    /**
     * Stops ResizeObserver and document refresh from calling `align`; keeps `window` resize (viewport / breakpoints).
     * @returns {void}
     */
    _finalizeAlignmentListeners() {
      if (this._alignmentFinalized) return;
      this._alignmentFinalized = true;

      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
        this._resizeObserver = null;
      }

      if (this._resizeTimeout) {
        clearTimeout(this._resizeTimeout);
        this._resizeTimeout = null;
      }

      document.removeEventListener('wlsn:align-items-height:refresh', this.bind.onDocRefresh);
    }

    /**
     * @param {boolean} force - When true, run even if alignment was already finalized (`refresh()` path).
     * @returns {void}
     */
    _maybeFinalizeAfterAlign(force) {
      if (force) return;
      this._finalizeAlignmentListeners();
    }

    /**
     * Document-level refresh: optional `detail.root` must be this element to run, or omit `root` to align all hosts.
     * @param {Event} evt - CustomEvent
     * @returns {void}
     */
    _onDocRefresh(evt) {
      if (this._alignmentFinalized) return;
      if (!(evt instanceof CustomEvent)) return;
      const targetRoot = evt.detail && evt.detail.root;
      if (targetRoot && targetRoot !== this) return;
      this.align();
    }

    /**
     * Debounced ResizeObserver callback.
     * @returns {void}
     */
    _onResizeObserver() {
      if (this._alignmentFinalized) return;
      if (typeof this._debouncedAlign === 'function') {
        this._debouncedAlign();
      }
    }

    /**
     * Debounced window resize handler (keeps working after finalize via `_alignImpl(this._alignmentFinalized)`).
     * @returns {void}
     */
    _onWindowResize() {
      if (typeof this._debouncedAlign === 'function') {
        this._debouncedAlign();
      }
    }

    /**
     * Clears inline heights on all `.js-align-item` nodes under this host.
     * @returns {void}
     */
    _resetHeights() {
      const { items } = this.elements;
      items.forEach((el) => {
        el.style.height = '';
      });
    }

    /**
     * Reads a positive integer from `data-wlsn-align-{suffix}` on this host.
     * @param {string} suffix - e.g. `mob`, `cols`
     * @returns {number}
     */
    _readAlignAttr(suffix) {
      const raw = this.getAttribute(`data-wlsn-align-${suffix}`);
      if (raw == null || String(raw).trim() === '') return NaN;
      const n = parseInt(String(raw).trim(), 10);
      if (Number.isNaN(n) || n < 1) return NaN;
      return n;
    }

    /**
     * @returns {'grid' | 'slider'}
     */
    _getLayout() {
      const v = (this.getAttribute('data-wlsn-layout') || 'grid').trim().toLowerCase();
      return v === 'slider' ? 'slider' : 'grid';
    }

    /**
     * @returns {boolean}
     */
    _hasColumnConfig() {
      const mob = this._readAlignAttr('mob');
      const tab = this._readAlignAttr('tab');
      const desk = this._readAlignAttr('desk');
      const fixed = this._readAlignAttr('cols');
      return !Number.isNaN(mob) || !Number.isNaN(tab) || !Number.isNaN(desk) || !Number.isNaN(fixed);
    }

    /**
     * @returns {boolean}
     */
    _useGlobalMaxAlign() {
      return !this._hasColumnConfig();
    }

    /**
     * @param {number} width - Viewport width (px)
     * @returns {number}
     */
    _getEffectiveColumns(width) {
      const mob = this._readAlignAttr('mob');
      const tab = this._readAlignAttr('tab');
      const desk = this._readAlignAttr('desk');
      const fixed = this._readAlignAttr('cols');

      const hasBreakpoint = !Number.isNaN(mob) || !Number.isNaN(tab) || !Number.isNaN(desk);

      if (!hasBreakpoint) {
        if (!Number.isNaN(fixed)) return fixed;
        return 1;
      }

      /** @type {number} */
      let cols = NaN;
      const bp = WLSNAlignItemsHeight.BREAKPOINTS;
      if (width >= bp.desk) {
        cols = desk;
        if (Number.isNaN(cols)) cols = tab;
        if (Number.isNaN(cols)) cols = mob;
        if (Number.isNaN(cols)) cols = fixed;
      } else if (width >= bp.tab) {
        cols = tab;
        if (Number.isNaN(cols)) cols = mob;
        if (Number.isNaN(cols)) cols = fixed;
      } else {
        cols = mob;
        if (Number.isNaN(cols)) cols = fixed;
      }

      if (Number.isNaN(cols) || cols < 1) return 1;
      return cols;
    }

    /**
     * @param {Iterable<HTMLElement>} elements - Nodes to measure
     * @returns {number}
     */
    _maxOffsetHeight(elements) {
      let max = 0;
      for (const el of elements) {
        const h = el.offsetHeight;
        if (h > max) max = h;
      }
      return max;
    }

    /**
     * @param {NodeListOf<HTMLElement> | HTMLElement[]} items - Item nodes
     * @returns {boolean}
     */
    _hasLaidOutItems(items) {
      for (let i = 0; i < items.length; i++) {
        if (items[i].offsetHeight > 0) return true;
      }
      return false;
    }

    /**
     * Row key for column layout.
     * @param {HTMLElement} el - Item element
     * @returns {string | null}
     */
    _rowKeyForItem(el) {
      const raw = el.getAttribute('data-index');
      if (raw != null && String(raw).trim() !== '') {
        return String(raw);
      }
      /** @type {HTMLElement | null} */
      const parent = el.closest(`.${this.classes.group}`);
      if (!parent) return null;
      const siblings = parent.querySelectorAll(`.${this.classes.item}`);
      const idx = Array.prototype.indexOf.call(siblings, el);
      return idx >= 0 ? String(idx) : null;
    }

    /**
     * @param {NodeListOf<HTMLElement> | HTMLElement[]} items - Items to align
     * @returns {void}
     */
    _applyColumnModeHeights(items) {
      /** @type {Map<string, HTMLElement[]>} */
      const byKey = new Map();
      items.forEach((el) => {
        const key = this._rowKeyForItem(el);
        if (key == null) return;
        if (!byKey.has(key)) byKey.set(key, []);
        byKey.get(key).push(el);
      });

      items.forEach((el) => {
        el.style.height = '';
      });

      /** @type {Map<string, number>} */
      const maxByKey = new Map();
      byKey.forEach((itemEls, key) => {
        maxByKey.set(key, this._maxOffsetHeight(itemEls));
      });

      byKey.forEach((itemEls, key) => {
        const height = maxByKey.get(key);
        if (height == null || height <= 0) return;
        itemEls.forEach((node) => {
          node.style.height = `${height}px`;
        });
      });
    }

    /**
     * @param {HTMLElement[]} list - Items in document order
     * @param {number} cols - Items per row
     * @returns {void}
     */
    _applyFlatGridHeights(list, cols) {
      list.forEach((el) => {
        el.style.height = '';
      });

      for (let start = 0; start < list.length; start += cols) {
        const row = list.slice(start, start + cols);
        const maxHeight = this._maxOffsetHeight(row);
        if (maxHeight <= 0) continue;
        row.forEach((node) => {
          node.style.height = `${maxHeight}px`;
        });
      }
    }

    /**
     * @param {NodeListOf<HTMLElement> | HTMLElement[]} items - Items
     * @returns {void}
     */
    _applyGlobalMaxHeights(items) {
      const list = Array.prototype.slice.call(items);
      list.forEach((el) => {
        el.style.height = '';
      });
      if (!list.length) return;
      const maxHeight = this._maxOffsetHeight(list);
      if (maxHeight <= 0) return;
      list.forEach((node) => {
        node.style.height = `${maxHeight}px`;
      });
    }

    /**
     * Computes and applies height rules for items under this host (no-op once finalized unless `refresh()` or window resize).
     * @returns {void}
     */
    align() {
      this._alignImpl(false);
    }

    /**
     * @param {boolean} force - Bypass finalized guard and skip re-finalizing listeners after this run.
     * @returns {void}
     */
    _alignImpl(force) {
      if (this._alignmentFinalized && !force) {
        return;
      }

      const { groups, items } = this.elements;
      if (!items.length) {
        this._resetHeights();
        return;
      }

      if (!this._hasLaidOutItems(items)) {
        this._resetHeights();
        return;
      }

      if (this._useGlobalMaxAlign()) {
        const list = Array.prototype.slice.call(items);
        const maxH = this._maxOffsetHeight(list);
        if (maxH > 0) {
          this._applyGlobalMaxHeights(items);
          this._maybeFinalizeAfterAlign(force);
        }
        return;
      }

      if (this._getLayout() === 'slider') {
        this._resetHeights();
        this._maybeFinalizeAfterAlign(force);
        return;
      }

      const width = typeof window !== 'undefined' ? window.innerWidth : WLSNAlignItemsHeight.BREAKPOINTS.desk;
      const cols = this._getEffectiveColumns(width);

      if (cols < 2) {
        this._resetHeights();
        this._maybeFinalizeAfterAlign(force);
        return;
      }

      if (groups.length < 2) {
        const list = Array.prototype.slice.call(items);
        this._applyFlatGridHeights(list, cols);
        this._maybeFinalizeAfterAlign(force);
        return;
      }

      this._applyColumnModeHeights(items);
      this._maybeFinalizeAfterAlign(force);
    }
  }

  customElements.define('wlsn-align-items-height', WLSNAlignItemsHeight);
}
